/**
 * Boli Live - Bid Slider Component
 *
 * Slide-to-bid interaction with theme-aware colors.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, useMotionValue, useTransform, useAnimation } from 'framer-motion';
import { ArrowRight, Check, Loader2, Sparkles } from 'lucide-react';
import { cn } from '../lib/utils';

interface BidSliderProps {
  amount: number;
  onConfirm: () => Promise<void>;
  disabled?: boolean;
  className?: string;
}

export function BidSlider({ amount, onConfirm, disabled = false, className }: BidSliderProps) {
  const [isConfirming, setIsConfirming] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const controls = useAnimation();

  const x = useMotionValue(0);
  const [containerWidth, setContainerWidth] = useState(0);

  const thumbSize = 56;
  const maxX = Math.max(0, containerWidth - thumbSize - 8);

  const progress = useTransform(x, [0, maxX], [0, 1]);
  const backgroundOpacity = useTransform(progress, [0, 0.8, 1], [0, 0.3, 0.5]);
  const textOpacity = useTransform(progress, [0, 0.5], [1, 0]);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const handleDragStart = useCallback(() => {
    if (disabled || isConfirming) return;
    if ('vibrate' in navigator) {
      navigator.vibrate(30);
    }
  }, [disabled, isConfirming]);

  const handleDragEnd = useCallback(async () => {
    if (disabled || isConfirming) return;

    const currentX = x.get();
    const threshold = maxX * 0.75;

    if (currentX >= threshold) {
      setIsConfirming(true);

      if ('vibrate' in navigator) {
        navigator.vibrate([50, 30, 50]);
      }

      await controls.start({ x: maxX });

      try {
        await onConfirm();
        setIsComplete(true);

        setTimeout(() => {
          setIsComplete(false);
          setIsConfirming(false);
          controls.start({ x: 0 });
        }, 2000);
      } catch (error) {
        setIsConfirming(false);
        controls.start({ x: 0, transition: { type: 'spring', stiffness: 400, damping: 30 } });
      }
    } else {
      controls.start({ x: 0, transition: { type: 'spring', stiffness: 400, damping: 30 } });
    }
  }, [disabled, isConfirming, maxX, onConfirm, controls, x]);

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <motion.div
      ref={containerRef}
      className={cn(
        'relative h-16 rounded-full overflow-hidden select-none',
        'bg-primary text-primary-foreground',
        'shadow-md',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {/* Animated background fill */}
      <motion.div
        className="absolute inset-0 bg-black/10"
        style={{ opacity: backgroundOpacity }}
      />

      {/* Shimmer effect */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute inset-y-0 w-20 bg-gradient-to-r from-transparent via-white/20 to-transparent"
          animate={{ x: [-100, containerWidth + 100] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
        />
      </div>

      {/* Track text */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center"
        style={{ opacity: textOpacity }}
      >
        <span className="font-bold text-sm tracking-wide flex items-center gap-2">
          {isComplete ? (
            <>
              <Sparkles className="w-4 h-4" />
              Bid Placed Successfully!
            </>
          ) : (
            `Slide to Bid ${formatAmount(amount)}`
          )}
        </span>
      </motion.div>

      {/* Draggable thumb */}
      <motion.div
        drag={disabled || isConfirming ? false : 'x'}
        dragConstraints={{ left: 0, right: maxX }}
        dragElastic={0}
        dragMomentum={false}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        animate={controls}
        style={{ x }}
        className={cn(
          'absolute top-1 left-1 w-14 h-14 rounded-full',
          'bg-white text-primary flex items-center justify-center',
          'cursor-grab active:cursor-grabbing',
          'shadow-lg',
          disabled && 'pointer-events-none'
        )}
        whileHover={{ scale: disabled ? 1 : 1.02 }}
        whileTap={{ scale: disabled ? 1 : 0.98 }}
      >
        {isConfirming ? (
          isComplete ? (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 500, damping: 25 }}
            >
              <Check className="w-6 h-6" />
            </motion.div>
          ) : (
            <Loader2 className="w-6 h-6 animate-spin" />
          )
        ) : (
          <motion.div
            animate={{ x: [0, 4, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <ArrowRight className="w-6 h-6" />
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
}
